// src/services/testimonial.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface Testimonial {
  id: string;
  userId: string;
  title: string;
  content: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  isFeatured: boolean;
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
  user?: {
    firstName: string;
    lastName: string;
    organizationName?: string;
    title?: string;
    photoUrl?: string;
  };
}

export interface TestimonialSubmission {
  title: string;
  content: string;
}

export interface TestimonialUpdate {
  title?: string;
  content?: string;
}

// ============================================
// PUBLIC API ENDPOINTS
// ============================================

/**
 * Get featured testimonials for homepage
 */
export async function getFeaturedTestimonials(limit?: number): Promise<Testimonial[]> {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());

  const { data } = await api.get(`/api/testimonials/featured?${params.toString()}`);
  return data.data.testimonials;
}

/**
 * Get all approved testimonials
 */
export async function getApprovedTestimonials(options?: {
  limit?: number;
  offset?: number;
}): Promise<{ testimonials: Testimonial[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  const { data } = await api.get(`/api/testimonials?${params.toString()}`);
  return data.data;
}

// ============================================
// USER API ENDPOINTS (Authenticated Users)
// ============================================

/**
 * Submit a new testimonial
 */
export async function submitTestimonial(testimonial: TestimonialSubmission): Promise<Testimonial> {
  const { data } = await api.post('/api/testimonials', testimonial);
  return data.data.testimonial;
}

/**
 * Get my testimonials
 */
export async function getMyTestimonials(): Promise<Testimonial[]> {
  const { data } = await api.get('/api/my/testimonials');
  return data.data.testimonials;
}

/**
 * Update my testimonial
 */
export async function updateMyTestimonial(id: string, updates: TestimonialUpdate): Promise<Testimonial> {
  const { data } = await api.patch(`/api/my/testimonials/${id}`, updates);
  return data.data.testimonial;
}

/**
 * Delete my testimonial
 */
export async function deleteMyTestimonial(id: string): Promise<void> {
  await api.delete(`/api/my/testimonials/${id}`);
}

// ============================================
// ADMIN API ENDPOINTS
// ============================================

/**
 * Get all testimonials (Admin only)
 */
export async function getAllTestimonials(options?: {
  status?: 'pending' | 'approved' | 'rejected';
  limit?: number;
  offset?: number;
}): Promise<{ testimonials: Testimonial[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams();
  if (options?.status) params.append('status', options.status);
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  const { data } = await api.get(`/api/admin/testimonials?${params.toString()}`);
  return data.data;
}

/**
 * Approve testimonial (Admin only)
 */
export async function approveTestimonial(id: string): Promise<Testimonial> {
  const { data } = await api.patch(`/api/admin/testimonials/${id}/approve`);
  return data.data.testimonial;
}

/**
 * Reject testimonial (Admin only)
 */
export async function rejectTestimonial(id: string, reason?: string): Promise<Testimonial> {
  const { data } = await api.patch(`/api/admin/testimonials/${id}/reject`, { reason });
  return data.data.testimonial;
}

/**
 * Update testimonial featured status (Admin only)
 */
export async function updateTestimonialFeatured(
  id: string,
  updates: { isFeatured?: boolean; displayOrder?: number }
): Promise<Testimonial> {
  const { data } = await api.patch(`/api/admin/testimonials/${id}/featured`, updates);
  return data.data.testimonial;
}

/**
 * Delete testimonial (Admin only)
 */
export async function deleteTestimonial(id: string): Promise<void> {
  await api.delete(`/api/admin/testimonials/${id}`);
}
